import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  Target, Plus, Pencil, Trash2, ChevronLeft, X, Check,
  Clock, Star, RotateCcw, Zap
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = window.location.origin;

const CATEGORIES = [
  { value: "putting", label: "Putting" },
  { value: "chipping", label: "Chipping" },
  { value: "pitching", label: "Pitching" },
  { value: "irons", label: "Iron Play" },
  { value: "driving", label: "Driving" },
  { value: "course", label: "Course Practice" }
];

const FREQUENCIES = [
  { value: "every_session", label: "Every Session", icon: <Zap className="w-4 h-4" /> },
  { value: "times_per_week", label: "X Times/Week", icon: <Clock className="w-4 h-4" /> },
  { value: "rotation", label: "Rotation", icon: <RotateCcw className="w-4 h-4" /> }
];

const PRIORITIES = [
  { value: "required", label: "Required", desc: "Always included" },
  { value: "preferred", label: "Preferred", desc: "When time allows" }
];

export default function CoachDrills() {
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "putting",
    description: "",
    instructions: "",
    duration_minutes: 10,
    frequency: "rotation",
    times_per_week: 2,
    priority: "preferred"
  });

  useEffect(() => {
    fetchDrills();
  }, []);

  const fetchDrills = async () => {
    try {
      const response = await authFetch(`${API}/api/coach-drills`, {
      });
      if (response.ok) {
        const data = await response.json();
        setDrills(data);
      }
    } catch (error) {
      console.error("Error fetching drills:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a drill name");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const url = editingDrill 
        ? `${API}/api/coach-drills/${editingDrill.drill_id}`
        : `${API}/api/coach-drills`;
      
      const method = editingDrill ? "PUT" : "POST";

      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingDrill ? "Drill updated" : "Drill added");
        setShowForm(false);
        setEditingDrill(null);
        resetForm();
        fetchDrills();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save drill");
      }
    } catch (error) {
      console.error("Error saving drill:", error);
      toast.error("Something went wrong");
    }
  };

  const handleDelete = async (drillId) => {
    if (!window.confirm("Delete this drill?")) return;

    try {
      const response = await authFetch(`${API}/api/coach-drills/${drillId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Drill deleted");
        fetchDrills();
      }
    } catch (error) {
      console.error("Error deleting drill:", error);
      toast.error("Failed to delete drill");
    }
  };

  const handleEdit = (drill) => {
    setEditingDrill(drill);
    setFormData({
      name: drill.name,
      category: drill.category,
      description: drill.description,
      instructions: drill.instructions || "",
      duration_minutes: drill.duration_minutes,
      frequency: drill.frequency,
      times_per_week: drill.times_per_week || 2,
      priority: drill.priority
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category: "putting",
      description: "",
      instructions: "",
      duration_minutes: 10,
      frequency: "rotation",
      times_per_week: 2,
      priority: "preferred"
    });
  };

  const openNewForm = () => {
    setEditingDrill(null);
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-[#555] hover:text-[#111]">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="font-bold text-lg text-[#111]">Coach Drills</h1>
              <p className="text-[#555] text-sm">Drills assigned by your coach</p>
            </div>
          </div>
          <Button
            data-testid="add-drill-btn"
            onClick={openNewForm}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Drill
          </Button>
        </div>
      </header>

      <main className="px-6 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Empty State */}
          {drills.length === 0 && !showForm && (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-emerald-700" />
                </div>
                <h3 className="text-lg font-semibold text-[#111] mb-2">No Coach Drills Yet</h3>
                <p className="text-[#555] mb-6">
                  Add drills your coach has assigned. They'll be automatically integrated into your practice sessions.
                </p>
                <Button onClick={openNewForm} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Your First Drill
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Drill List */}
          {drills.length > 0 && !showForm && (
            <div className="space-y-3">
              {drills.map((drill) => (
                <Card key={drill.drill_id} className="bg-white border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-[#111] truncate">{drill.name}</h3>
                          <Badge className={`
                            text-xs shrink-0
                            ${drill.priority === "required" 
                              ? "bg-red-500/20 text-red-400 border-red-500/30" 
                              : "bg-emerald-500/20 text-emerald-700 border-emerald-500/30"}
                          `}>
                            {drill.priority === "required" ? "Required" : "Preferred"}
                          </Badge>
                        </div>
                        <p className="text-[#555] text-sm mb-2 line-clamp-2">{drill.description}</p>
                        <div className="flex items-center gap-3 text-xs text-[#555]">
                          <span className="capitalize">{drill.category}</span>
                          <span>•</span>
                          <span>{drill.duration_minutes} min</span>
                          <span>•</span>
                          <span className="capitalize">{drill.frequency.replace("_", " ")}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleEdit(drill)}
                          className="p-2 text-[#555] hover:text-[#111] transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(drill.drill_id)}
                          className="p-2 text-[#555] hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Add/Edit Form */}
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card className="bg-white border-gray-200">
                  <CardHeader className="border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-[#111]">
                        {editingDrill ? "Edit Drill" : "Add Coach Drill"}
                      </CardTitle>
                      <button 
                        onClick={() => {
                          setShowForm(false);
                          setEditingDrill(null);
                          resetForm();
                        }}
                        className="text-[#555] hover:text-[#111]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    {/* Name */}
                    <div>
                      <Label className="text-[#111] mb-2 block">Drill Name</Label>
                      <Input
                        data-testid="drill-name-input"
                        placeholder="e.g., Gate Putting Drill"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] placeholder:text-[#555]"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <Label className="text-[#111] mb-3 block">Category</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {CATEGORIES.map((cat) => (
                          <button
                            key={cat.value}
                            onClick={() => setFormData({ ...formData, category: cat.value })}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              formData.category === cat.value
                                ? "bg-emerald-500 text-[#111]"
                                : "bg-gray-100 text-[#555] hover:bg-gray-200"
                            }`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <Label className="text-[#111] mb-2 block">Description / Instructions</Label>
                      <Textarea
                        data-testid="drill-description-input"
                        placeholder="Describe the drill and how to perform it..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] placeholder:text-[#555] min-h-[100px]"
                      />
                    </div>

                    {/* Duration */}
                    <div>
                      <Label className="text-[#111] mb-3 block">Duration (minutes)</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[5, 10, 15, 20].map((min) => (
                          <button
                            key={min}
                            onClick={() => setFormData({ ...formData, duration_minutes: min })}
                            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              formData.duration_minutes === min
                                ? "bg-emerald-500 text-[#111]"
                                : "bg-gray-100 text-[#555] hover:bg-gray-200"
                            }`}
                          >
                            {min} min
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Frequency */}
                    <div>
                      <Label className="text-[#111] mb-3 block">Frequency</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {FREQUENCIES.map((freq) => (
                          <button
                            key={freq.value}
                            onClick={() => setFormData({ ...formData, frequency: freq.value })}
                            className={`py-3 px-3 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-1 ${
                              formData.frequency === freq.value
                                ? "bg-emerald-500 text-[#111]"
                                : "bg-gray-100 text-[#555] hover:bg-gray-200"
                            }`}
                          >
                            {freq.icon}
                            <span>{freq.label}</span>
                          </button>
                        ))}
                      </div>
                      {formData.frequency === "times_per_week" && (
                        <div className="mt-3">
                          <Label className="text-[#555] text-sm mb-2 block">Times per week</Label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <button
                                key={n}
                                onClick={() => setFormData({ ...formData, times_per_week: n })}
                                className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                                  formData.times_per_week === n
                                    ? "bg-emerald-500 text-[#111]"
                                    : "bg-gray-100 text-[#555] hover:bg-gray-200"
                                }`}
                              >
                                {n}x
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Priority */}
                    <div>
                      <Label className="text-[#111] mb-3 block">Priority</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {PRIORITIES.map((p) => (
                          <button
                            key={p.value}
                            onClick={() => setFormData({ ...formData, priority: p.value })}
                            className={`p-4 rounded-xl text-left transition-all border ${
                              formData.priority === p.value
                                ? p.value === "required"
                                  ? "bg-red-500/20 border-red-500/50"
                                  : "bg-emerald-500/20 border-emerald-500/50"
                                : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-[#111]">{p.label}</span>
                              {formData.priority === p.value && (
                                <Check className={`w-4 h-4 ${p.value === "required" ? "text-red-400" : "text-emerald-700"}`} />
                              )}
                            </div>
                            <span className="text-xs text-[#555]">{p.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowForm(false);
                          setEditingDrill(null);
                          resetForm();
                        }}
                        className="text-[#555]"
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="save-drill-btn"
                        onClick={handleSubmit}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        {editingDrill ? "Save Changes" : "Add Drill"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Info Card */}
          {drills.length > 0 && !showForm && (
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <p className="text-emerald-700 text-sm">
                  <strong>How it works:</strong> Required drills are always included in your sessions. 
                  Preferred drills rotate naturally based on available time.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
